import React from 'react';
import { useInView } from '../hooks/useInView';

const Features: React.FC = () => {
  const [headingRef, headingInView] = useInView();
  const [gridRef, gridInView] = useInView();

  const features = [
    {
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
        </svg>
      ),
      title: "Part Highlighting",
      description: "Single-button part highlighting. Just one click to bring a part into focus.",
      color: "from-blue-500 to-blue-700"
    },
    {
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
        </svg>
      ),
      title: "Intelligent Part Muting",
      description: "Practice your part in isolation or with specific combinations of other voices. Perfect for learning difficult sections.",
      color: "from-blue-500 to-blue-700"
    },
    {
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
        </svg>
      ),
      title: "Multi-Platform Support",
      description: "Access your practice tracks on any device. Works seamlessly on desktop, tablet, and mobile.",
      color: "from-blue-500 to-blue-700"
    },
    {
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
      ),
      title: "Lightning Fast",
      description: "Built as a modern web app, it's fast and responsive, complete with a modern UI.",
      color: "from-blue-500 to-blue-700"
    },
    {
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
        </svg>
      ),
      title: "PWA Support",
      description: "Add ChoralPlayer to your home screen for a seamless experience.",
      color: "from-blue-500 to-blue-700"
    },
    {
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
        </svg>
      ),
      title: "Personalized Experience",
      description: "Every single track is hand-crafted by ChoralPlayer to ensure a high-quality experience every single time.",
      color: "from-blue-500 to-blue-700"
    }
  ];

  return (
    <section id="features" className="py-20 px-4 bg-white">
      <div className="container mx-auto">
        <div
          ref={headingRef}
          className={`text-center mb-16 ${headingInView ? 'animate-fade-up' : 'opacity-0'}`}
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Everything You Need for
            <span className="gradient-text"> Perfect Practice</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            ChoralPlayer combines cutting-edge technology with intuitive design to create
            the ultimate practice experience for choirs of all levels.
          </p>
        </div>

        <div ref={gridRef} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {features.map((feature, index) => (
            <div
              key={index}
              className={`card hover:shadow-xl transition-all duration-300 hover:-translate-y-2 ${gridInView ? 'animate-fade-up' : 'opacity-0'}`}
              style={{ animationDelay: gridInView ? `${index * 80}ms` : undefined }}
            >
              <div className={`w-16 h-16 bg-gradient-to-br ${feature.color} rounded-xl flex items-center justify-center mb-6`}>
                <div className="text-white">
                  {feature.icon}
                </div>
              </div>
              <h3 className="text-xl font-semibold mb-4">{feature.title}</h3>
              <p className="text-gray-600 leading-relaxed">{feature.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Features;
